// BubbleSelect — multi-select pill cloud
// - readOnly mode: shows selected pills only, no interaction
// - Truncation: show first 12 by default, [More +N] [Add] bottom-right
// - Quick-add popover per field
// - Color mode: single-click = add/remove, double-click = set as primary
//   Badges: P (primary), 2, 3, 4... (position) always visible on selected

import { useState, useRef, useEffect } from 'react'
import { IconPlus, IconX, IconCheck } from '@tabler/icons-react'
import { fieldOptionsApi } from '../../lib/api.ts'

type Option = { value: string; label: string; hex?: string }

type QuickAddConfig = {
  attribute: string
  isColor?: boolean
  onAdded: (option: Option & { id: string }) => void
}

type Props = {
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  max?: number
  colorMode?: boolean
  quickAdd?: QuickAddConfig
  maxVisible?: number
  readOnly?: boolean
}

// ── Color helpers ─────────────────────────────────────────────────────────────

function contrastColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#1a1a1a' : '#ffffff'
}

function hexToName(hex: string): string {
  return hex.toUpperCase()
}

// ── Position badge ────────────────────────────────────────────────────────────

function PositionBadge({ pos }: { pos: number }) {
  const label = pos === 1 ? 'P' : String(pos)
  return (
    <span
      className={[
        'absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 rounded-full text-[9px] font-bold flex items-center justify-center leading-none z-10 shadow-md',
        pos === 1
          ? 'bg-brand-orange text-white border border-white/20'
          : 'bg-zinc-800 text-zinc-200 border border-zinc-600',
      ].join(' ')}>
      {label}
    </span>
  )
}

// ── Quick-add popover ─────────────────────────────────────────────────────────

function QuickAddPopover({ config, onClose }: { config: QuickAddConfig; onClose: () => void }) {
  const [name, setName] = useState('')
  const [hex, setHex] = useState('#888888')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [nameManual, setNameManual] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { inputRef.current?.focus() }, [])

  const displayName = config.isColor && !nameManual ? hexToName(hex) : name

  const handleSave = async () => {
    const finalName = displayName.trim()
    if (!finalName) { setError('Name is required'); return }
    setSaving(true); setError('')
    try {
      const body: Record<string, string> = { displayLabel: finalName }
      if (config.isColor) body.hexValue = hex
      const res = await fieldOptionsApi.quickAdd(config.attribute, body)
      config.onAdded({
        id: res.data.id, value: res.data.value,
        label: res.data.displayLabel, hex: res.data.hexValue ?? undefined
      })
      onClose()
    } catch (e: any) {
      setError(e.message ?? 'Failed to add')
    } finally { setSaving(false) }
  }

  return (
    <div className="absolute z-50 top-full right-0 mt-1.5 w-56 bg-zinc-900 border border-border-strong rounded-xl shadow-2xl p-3 space-y-2.5">
      <div className="absolute -top-1.5 right-4 w-3 h-3 bg-zinc-900 border-l border-t border-border-strong rotate-45" />

      {config.isColor && (
        <div className="flex items-center gap-2">
          <input type="color" value={hex}
            onChange={e => { setHex(e.target.value); if (!nameManual) setName('') }}
            className="w-9 h-9 rounded-lg cursor-pointer border border-border-default bg-transparent shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-zinc-500 mb-0.5">Preview</div>
            <div className="px-2 py-1 rounded-full text-xs font-medium truncate"
              style={{ backgroundColor: hex, color: contrastColor(hex) }}>
              {displayName || hexToName(hex)}
            </div>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        value={config.isColor && !nameManual ? '' : name}
        onChange={e => { setName(e.target.value); if (config.isColor) setNameManual(true) }}
        onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onClose() }}
        placeholder={config.isColor ? `Name (default: ${hexToName(hex)})` : 'Name...'}
        className="w-full px-2.5 py-1.5 bg-surface-raised border border-border-default rounded-lg text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:border-brand-orange/60 transition-colors" />

      {error && <p className="text-[10px] text-red-400">{error}</p>}

      <div className="flex items-center gap-1.5">
        <button onClick={onClose}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs border border-border-default text-zinc-500 hover:text-zinc-300 transition-colors">
          <IconX size={11} /> Cancel
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-50 transition-colors">
          <IconCheck size={11} /> {saving ? '...' : 'Add'}
        </button>
      </div>
    </div>
  )
}

// ── Main BubbleSelect ─────────────────────────────────────────────────────────

export function BubbleSelect({
  options, selected, onChange, max, colorMode,
  quickAdd, maxVisible = 12, readOnly = false
}: Props) {
  const [expanded, setExpanded] = useState(false)
  const [showQuickAdd, setShowQuickAdd] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const lastClickRef = useRef<{ value: string; time: number } | null>(null)

  const displayOptions = readOnly
    ? options
        .filter(o => selected.includes(o.value))
        .sort((a, b) => selected.indexOf(a.value) - selected.indexOf(b.value))
    : (expanded ? options : options.slice(0, maxVisible))

  const hiddenCount = options.length - maxVisible

  useEffect(() => {
    if (!showQuickAdd) return
    const h = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowQuickAdd(false)
      }
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [showQuickAdd])

  // Standard toggle — add/remove
  const toggle = (value: string) => {
    if (readOnly) return
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value))
    } else {
      if (max && selected.length >= max) return
      onChange([...selected, value])
    }
  }

  // Color mode: single click = toggle, double-click = set as primary (move to front)
  const handleColorClick = (value: string) => {
    if (readOnly) return
    const now = Date.now()
    const last = lastClickRef.current

    if (last && last.value === value && now - last.time < 400) {
      // Double-click — set as primary
      lastClickRef.current = null
      if (selected.includes(value)) {
        onChange([value, ...selected.filter(v => v !== value)])
      }
      return
    }

    lastClickRef.current = { value, time: now }
    toggle(value)
  }

  const handleQuickAdded = (option: Option & { id: string }) => {
    onChange([...selected, option.id])
    setShowQuickAdd(false)
  }

  if (readOnly && displayOptions.length === 0) {
    return <span className="text-sm text-zinc-600 italic">None selected</span>
  }

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-2">
        {displayOptions.map(opt => {
          const isSelected = selected.includes(opt.value)
          const pos = isSelected ? selected.indexOf(opt.value) + 1 : null

          if (colorMode && opt.hex) {
            const fg = contrastColor(opt.hex)
            return (
              <div key={opt.value} className="relative">
                {pos !== null && <PositionBadge pos={pos} />}
                <button type="button"
                  onClick={() => handleColorClick(opt.value)}
                  disabled={readOnly}
                  title={pos === null ? 'Click to add' : pos === 1 ? 'Primary color (double-click another to change)' : 'Double-click to make primary'}
                  className={[
                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm border-2 transition-all',
                    !readOnly && isSelected ? 'ring-2 ring-brand-orange/60 ring-offset-1 ring-offset-surface-base scale-105' : '',
                    !readOnly && !isSelected ? 'opacity-80 hover:opacity-100 hover:scale-105' : '',
                    readOnly ? 'cursor-default' : '',
                  ].join(' ')}
                  style={{
                    backgroundColor: opt.hex,
                    color: fg,
                    borderColor: isSelected ? '#E07A20' : 'transparent',
                  }}>
                  <span className="text-xs font-medium">{opt.label}</span>
                  {!readOnly && isSelected && <IconCheck size={11} strokeWidth={2.5} />}
                </button>
              </div>
            )
          }

          return (
            <div key={opt.value} className="relative">
              {isSelected && pos !== null && <PositionBadge pos={pos} />}
              <button type="button"
                onClick={() => toggle(opt.value)}
                disabled={readOnly}
                className={[
                  'px-3 py-1.5 rounded-full text-sm border transition-all',
                  isSelected
                    ? 'bg-brand-orange border-brand-orange text-white font-medium'
                    : 'border-zinc-500 text-zinc-200 hover:border-zinc-300 hover:text-white',
                  readOnly ? 'cursor-default' : '',
                ].join(' ')}>
                {opt.label}
              </button>
            </div>
          )
        })}

        {/* More +N — only when truncated, orange outer-edge badge style */}
        {!readOnly && !expanded && hiddenCount > 0 && (
          <div className="relative">
            <button type="button" onClick={() => setExpanded(true)}
              className="px-2.5 py-1.5 rounded-full text-sm border border-dashed border-zinc-600 text-zinc-500 hover:border-zinc-400 hover:text-zinc-300 transition-colors pr-5">
              More
            </button>
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-orange text-white text-[9px] font-bold flex items-center justify-center leading-none z-10">
              {hiddenCount}
            </span>
          </div>
        )}

        {!readOnly && expanded && hiddenCount > 0 && (
          <button type="button" onClick={() => setExpanded(false)}
            className="px-2.5 py-1.5 rounded-full text-sm border border-dashed border-zinc-600 text-zinc-500 hover:border-zinc-400 hover:text-zinc-300 transition-colors">
            Less
          </button>
        )}

        {/* Add button */}
        {!readOnly && quickAdd && (
          <div className="relative" ref={popoverRef}>
            <button type="button" onClick={() => setShowQuickAdd(v => !v)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm border border-dashed border-zinc-600 text-zinc-500 hover:border-zinc-400 hover:text-zinc-300 transition-colors">
              <IconPlus size={12} /> Add
            </button>
            {showQuickAdd && (
              <QuickAddPopover
                config={{ ...quickAdd, onAdded: handleQuickAdded }}
                onClose={() => setShowQuickAdd(false)} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
